import { getEnumRushStatusName } from '../autogen/bundle.js';

import { getStrWithLowFirstLetter } from '../util.js';
import { isRoleAdmin              } from '../util.js';
import { getOptionalTextMomentStr } from '../util.js';
import { getDurationStr           } from '../util.js';
import { getDurationStrSimple     } from '../util.js';
import { isNumberSpecified        } from '../util.js';

/**
 * @typedef { import('../auth.js').Role } Role
 */

/**
 * @typedef { import('./event.js').PlayerGroupId } PlayerGroupId
 */

//-----------------------------------------------------------------------------

/**
 * @typedef {number} RushStatus
 */

// see EnumRushStatus
export const RUSH_STATUS__ACTIVE   = 1;
export const RUSH_STATUS__FINISHED = 2;
export const RUSH_STATUS__INCOMING = 3;
export const RUSH_STATUS__CLOSED   = 4;

/**
 * @param {RushStatus} status
 * @returns {boolean}
 */
export function isRushIncoming(status) {
  return status === RUSH_STATUS__INCOMING;
}

/**
 * @param {RushStatus} status
 * @returns {boolean}
 */
export function isRushActive(status) {
  return status === RUSH_STATUS__ACTIVE;
}

/**
 * @param {RushStatus} status
 * @returns {boolean}
 */
export function isRushFinished(status) {
  return status === RUSH_STATUS__FINISHED;
}

/**
 * @param {RushStatus} status
 * @returns {boolean}
 */
export function isRushClosed(status) {
  return status === RUSH_STATUS__CLOSED;
}

/**
 * @param {number} status
 * @returns {string}
 */
export function getRushStatusName(status) {
  const name = getEnumRushStatusName(status);
  return getStrWithLowFirstLetter(name);
}

/**
 * @param {RushStatus} status
 * @returns {Object}
 */
export function getRushStatusClass(status) {
  return {
    activeRush:   isRushActive(status),
    finishedRush: isRushFinished(status),
  };
}

// options for component b-form-select
export function getRushStatusOptionsForFilter() {
  const options = getRushStatusOptions();

  const nullOption = {
    value: null,
    text: 'any',
  };
  options.unshift(nullOption);

  return options;
}

function getRushStatusOptions() {
  const options = [];

  const statusList = getRushStatuses();
  for(const status of statusList) {
    const option = {
      value: status,
      text: getRushStatusName(status),
    };
    options.push(option);
  }

  return options;
}

// Статусы перечислены в том порядке,
// в котором они должны быть показаны в инсайдере
// на странице всех ражей в фильтре.
function getRushStatuses() {
  return [
    RUSH_STATUS__INCOMING,
    RUSH_STATUS__ACTIVE,
    RUSH_STATUS__FINISHED,
    RUSH_STATUS__CLOSED,
  ];
}

//-----------------------------------------------------------------------------

/**
 * @param {RushStatus} status
 * @returns {boolean}
 */
export function isRushEditable(status) {
  return isRushStimeEditable(status) || isRushFtimeEditable(status);
}

/**
 * @param {RushStatus} status
 * @returns {boolean}
 */
export function isRushStimeEditable(status) {
  return isRushIncoming(status);
}

/**
 * @param {RushStatus} status
 * @returns {boolean}
 */
export function isRushFtimeEditable(status) {
  return isRushIncoming(status) || isRushActive(status);
}

//-----------------------------------------------------------------------------

/**
 * @param {Object} rush
 * @returns {number}
 */
export function getRushPlannedDuration(rush) {
  return rush.ftime - rush.stime;
}

/**
 * @param {Object} rush
 * @returns {string}
 */
export function getRushPlannedDurationStr(rush) {
  const plannedDuration = getRushPlannedDuration(rush);
  return getDurationStr(plannedDuration);
}

//-----------------------------------------------------------------------------

/**
 * @param {Role} role
 * @returns {boolean}
 */
export function mayManageRush(role) {
  return isRoleAdmin(role);
}

//-----------------------------------------------------------------------------

/**
 * @typedef {Object} RushPreview
 * @property {number}        id
 * @property {PlayerGroupId} playerGroupId
 * @property {RushStatus}    status
 * @property {number}        stime
 * @property {number}        srtime
 * @property {number}        ftime
 * @property {number}        frtime
 */

/**
 * @typedef {Object} RushPreviewContainer
 * @property {RushPreview} rush
 * @property {boolean}     selected
 */

//-----------------------------------------------------------------------------

/**
 * @typedef {Object} Rush
 * @property {number}        id
 * @property {PlayerGroupId} playerGroupId
 * @property {number}        clientMinVersionCode
 * @property {RushStatus}    status
 * @property {number}        ctime
 * @property {number}        stime
 * @property {number}        srtime
 * @property {number}        ftime
 * @property {number}        frtime
 */

//-----------------------------------------------------------------------------

/**
 * @param {number} srtime
 * @returns {string}
 */
export function getRushRealStartTimeStr(srtime) {
  return getOptionalTextMomentStr(srtime, 'not started');
}

/**
 * @param {number} frtime
 * @returns {string}
 */
export function getRushRealFinishTimeStr(frtime) {
  return getOptionalTextMomentStr(frtime, 'not finished');
}

/**
 * @param {Object} rush
 * @param {number} nowSec
 * @returns {string}
 */
export function getRushRemainingTimeStr(rush, nowSec) {
  if(rush.status === RUSH_STATUS__INCOMING) {
    return 'incoming';
  }

  if(rush.status === RUSH_STATUS__FINISHED) {
    return 'finished';
  }

  const timeEnded = nowSec >= rush.ftime;
  if(timeEnded) {
    return 'time ended';
  }

  const remainintTime = rush.ftime - nowSec;
  return getDurationStrSimple(remainintTime);
}

/**
 * @param {Object} rush
 * @returns {string}
 */
export function getRushRealDurationStrSimple(rush) {
  if(!isNumberSpecified(rush.srtime)) {
    return 'not started';
  }
  if(!isNumberSpecified(rush.frtime)) {
    return 'not finished';
  }
  const realDuration = rush.frtime - rush.srtime;
  return getDurationStrSimple(realDuration);
}

//-----------------------------------------------------------------------------
